// Fx — transient display events + the notification pane (top-right chrome
// column slot above the info pane). Chat-log order: newest at the BOTTOM,
// keeps the last 30 (oldest drop off the top), view stays PINNED to the
// bottom on arrivals unless the reader deliberately scrolled up (standard
// console autoscroll). Entries are a permanent log — clicking one does
// NOTHING (only the 30-cap trims them). Per-entry significance stripe +
// title color: level_up GOLD, discovered DISCOVERY, quest C_QUEST,
// item_broke DANGER, alert WARN — never ACCENT.

import { el } from './kit';
import * as vsj from './vsj';
import type { Fx } from './types';

const MAX_ENTRIES = 30;

type FxListener = (fx: Fx) => void;
const typeListeners = new Map<string, FxListener[]>();

export function onFxType(type: string, fn: FxListener): void {
  const arr = typeListeners.get(type) ?? [];
  arr.push(fn);
  typeListeners.set(type, arr);
}

let listNode: HTMLElement | null = null;

export function buildPane(): HTMLElement {
  listNode = el('div', { class: 'notif-list k-scroll' });
  return el(
    'div',
    { class: 'k-panel notif-pane' },
    el('div', {
      class: 'k-faint k-small',
      style: 'padding:3px 8px;border-bottom:1px solid var(--divider)',
      text: 'Notifications',
    }),
    listNode,
  );
}

function colorFor(fx: Fx): { stripe: string; title: string } {
  switch (fx.fx) {
    case 'level_up':
      return { stripe: 'var(--gold)', title: 'var(--gold)' };
    case 'discovered': // codex first-seen: its own discovery gold
      return { stripe: 'var(--discovery)', title: 'var(--discovery)' };
    case 'quest_started':
    case 'quest_step_done':
    case 'quest_complete':
      return { stripe: 'var(--c-quest)', title: 'var(--c-quest)' };
    case 'item_broke':
    case 'death':
      return { stripe: 'var(--danger)', title: 'var(--danger)' };
    case 'alert':
      return { stripe: 'var(--warn)', title: 'var(--warn)' };
    default:
      return { stripe: 'var(--divider)', title: 'var(--text)' };
  }
}

function lines(fx: Fx): [string, string] | null {
  const f = fx as any;
  switch (fx.fx) {
    case 'items_gained':
    case 'xp_gained':
      return null; // flight/floater-only tickers, too chatty for the pane
    case 'level_up': {
      const unlocks: string[] = f.unlocks ?? (f.unlocked ? [f.unlocked] : []);
      const extra = unlocks.length ? ` — unlocked ${unlocks.join(', ')}` : '';
      return ['Level up!', `${f.skill ?? ''} is now ${f.level ?? '?'}${extra}`];
    }
    case 'item_broke': {
      // display NAME via the infos registry (raw ids never shown); the served
      // `consequence` prose (what breaking it means) rides along verbatim
      const broke = f.name ?? (f.item != null ? vsj.infoName(String(f.item)) : null) ?? 'An item';
      const cons = f.consequence != null ? ` ${f.consequence}` : '';
      return ['Broke', `${broke} broke.${cons}`];
    }
    case 'kill': {
      // served shape is {fx:'kill', creature, xp, cid}
      const xp = f.xp != null ? ` (+${f.xp} combat xp)` : '';
      return ['Slain', `${f.creature ?? f.name ?? 'The foe'} is slain${xp}`];
    }
    case 'death':
      // served death prose when present
      return ['Death', String(f.text ?? 'You died.')];
    case 'fled':
      return ['Fled', 'You slipped away.'];
    case 'quest_started':
      return ['Quest started', String(f.name ?? f.quest ?? '')];
    case 'quest_step_done': {
      const next = f.next ? ` — next: ${f.next}` : '';
      return ['Quest progress', `${f.text ?? 'Step done'}${next}`];
    }
    case 'quest_complete':
      return ['Quest complete', String(f.name ?? f.quest ?? '')];
    case 'alert':
      return ['Notice', String(f.text ?? '')];
    case 'arrived':
      return ['Arrived', String(f.name ?? '')];
    case 'gather_empty':
      return ['Nothing', 'Found nothing this cycle.'];
    case 'discovered':
      return ['Discovered', `${f.name ?? 'A beast'} enters your Codex.`];
    default:
      return null;
  }
}

export function handleFx(list: Fx[]): void {
  for (const fx of list) {
    for (const fn of typeListeners.get(fx.fx) ?? []) fn(fx);
    for (const fn of typeListeners.get('*') ?? []) fn(fx);
    addEntry(fx);
  }
}

export function alert(text: string): void {
  addEntry({ fx: 'alert', text });
}

function addEntry(fx: Fx): void {
  if (!listNode) return;
  const content = lines(fx);
  if (!content) return;
  const [title, body] = content;
  const { stripe, title: titleColor } = colorFor(fx);
  // pinned = the reader is (within a few px of) the bottom before the insert
  const pinned = listNode.scrollHeight - listNode.scrollTop - listNode.clientHeight < 8;
  const entry = el(
    'div',
    { class: 'notif-entry' },
    el('div', { class: 'stripe', style: `background:${stripe}` }),
    el('span', { style: `color:${titleColor};flex:none`, text: title }),
    el('span', { class: 'k-dim', text: body }),
  );
  listNode.append(entry);
  let removed = 0; // height trimmed off the top (oldest entries)
  while (listNode.children.length > MAX_ENTRIES) {
    const first = listNode.firstElementChild as HTMLElement;
    removed += first.offsetHeight;
    first.remove();
  }
  // autoscroll: follow the newest only when already at the bottom — a reader
  // who deliberately scrolled up keeps their place (trims above the viewport
  // shift the scroll by exactly what was removed)
  if (pinned) listNode.scrollTop = listNode.scrollHeight;
  else if (removed) listNode.scrollTop = Math.max(0, listNode.scrollTop - removed);
}

// ---------- floaters ----------
export function floatText(anchor: Element | null, text: string, color: string): void {
  const r = anchor?.getBoundingClientRect();
  const x = r ? r.left + r.width / 2 : innerWidth / 2;
  const y = r ? r.top + 10 : innerHeight / 2;
  const f = el('div', {
    class: 'floater',
    style: `left:${x}px;top:${y}px;color:${color};transform:translateX(-50%)`,
    text,
  });
  document.body.append(f);
  setTimeout(() => f.remove(), 1400);
}
